"use client";

import { useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import type { Locale } from "@/lib/i18n/types";
import { getMessages } from "@/lib/i18n";
import { submitSsoPlant } from "@/lib/sso/plant";
import { AuthMfaModal } from "@/components/catalogs/CUserAccounts/AuthMfaModal";
import styles from "./GlobalLoginGate.module.css";

/**
 * Pełnoekranowy lockout logowania dla flagi "Wymagaj zalogowania dla dostępu do
 * strony" (`cms_login_required` / `clientapi_after_loggin`).
 *
 * Zastępuje CAŁĄ stronę - bez chrome (nagłówek/stopka), okien i treści. Niezalogowany
 * odwiedzający widzi WYŁĄCZNIE komunikat + formularz logowania. Login idzie przez
 * `/api/iiapi/login` (współdzielony backend auth, niezależny od katalogu
 * `c_user_accounts`), które ustawia cookie `imperial_frontend_session`; po sukcesie
 * odświeżamy server components -> gate przepuszcza i renderuje właściwą stronę.
 */
export function GlobalLoginGate({ locale }: { locale: Locale }) {
  const t = getMessages(locale).globalLoginGate;
  const router = useRouter();

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);

  //Wspolna finalizacja post-login (SSO plant + refresh) - reuse przez direct
  //login i sukces AuthMfaModal (drugi krok gdy user ma 2FA).
  const finishLoginRedirect = (plant?: { ticket: string; action: string }) => {
    if (plant?.ticket && plant?.action) {
      if (submitSsoPlant(plant.action, plant.ticket, window.location.href)) {
        return;
      }
    }
    router.refresh();
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);

    try {
      const res = await fetch("/api/iiapi/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: login.trim(), password }),
      });

      const body = (await res.json().catch(() => null)) as
        | {
            ok?: boolean;
            message?: string;
            plant?: { ticket: string; action: string };
            mfa_required?: boolean;
          }
        | null;

      if (!res.ok) {
        setError(body?.message ?? t.failed);
        setBusy(false);
        return;
      }

      //MFA required - otwieramy modal. Cookie challenge ustawiony przez route
      //handler; modal wysyla kod przez /api/iiapi/login/mfa. Success -> finish.
      if (body?.mfa_required === true) {
        setShowMfaModal(true);
        setBusy(false);
        return;
      }

      finishLoginRedirect(body?.plant);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <main className={styles.root}>
      {showMfaModal && (
        <AuthMfaModal
          onSuccess={(result) => {
            setShowMfaModal(false);
            finishLoginRedirect(result.plant);
          }}
          onCancel={() => setShowMfaModal(false)}
        />
      )}
      <div className={styles.card}>
        <div className={styles.iconWrap} aria-hidden="true">
          <svg
            className={styles.icon}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h1 className={styles.title}>{t.title}</h1>
        <p className={styles.message}>{t.message}</p>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {error !== null && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          <label className={styles.label} htmlFor="global-login-user">
            {t.loginLabel}
          </label>
          <input
            id="global-login-user"
            className={styles.input}
            type="text"
            autoComplete="username"
            autoFocus
            required
            value={login}
            onChange={(e) => setLogin(e.target.value)}
          />

          <label className={styles.label} htmlFor="global-login-pass">
            {t.passwordLabel}
          </label>
          <input
            id="global-login-pass"
            className={styles.input}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button className={styles.button} type="submit" disabled={busy}>
            {busy ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </main>
  );
}
